import {
  ForecastPrintingService,
  ForecastService,
  type PrintDueList,
} from "@common/client"
import { aircraftBaseKey } from "./aircraft"
import { makeAxiosMutation, makeQuery, parseBlobErrors } from "./utils"
import { workPackagesKey } from "./work-packages"

export const forecastsBaseKey = ["forecasts"] as const

export const forecastsKey = (
  params: Parameters<typeof ForecastService.listForecasts>[0],
) => [...forecastsBaseKey, "list", params] as const

export const forecastKey = (
  params: Parameters<typeof ForecastService.getForecast>[0],
) => [...forecastsBaseKey, "detail", params] as const

export const forecastSummariesKey = (
  params: Parameters<typeof ForecastService.listForecastSummaries>[0],
) => [...forecastsBaseKey, "summaries", params] as const

export const forecastPreviewKey = (
  params: Parameters<typeof ForecastService.previewForecast>[0],
) => [...forecastsBaseKey, "preview", params] as const

export const forecastsQuery = makeQuery({
  serviceCall: ForecastService.listForecasts,
  queryKey: forecastsKey,
})

export const forecastSummariesQuery = makeQuery({
  serviceCall: ForecastService.listForecastSummaries,
  queryKey: forecastSummariesKey,
})

export const forecastQuery = makeQuery({
  serviceCall: ForecastService.getForecast,
  queryKey: forecastKey,
})

export const forecastPreviewQuery = makeQuery({
  serviceCall: ForecastService.previewForecast,
  queryKey: forecastPreviewKey,
})

export const useCreateForecastMutation = makeAxiosMutation({
  serviceCall: ForecastService.createForecast,
  invalidates: () => [forecastsBaseKey],
})

export const useUpdateForecastMutation = makeAxiosMutation({
  serviceCall: ForecastService.updateForecast,
  invalidates: ({ forecastUpdate }) =>
    forecastUpdate.scope
      ? [forecastsBaseKey, aircraftBaseKey]
      : [forecastsBaseKey],
})

export const useArchiveForecastMutation = makeAxiosMutation({
  serviceCall: ForecastService.archiveForecast,
  invalidates: () => [forecastsBaseKey, aircraftBaseKey],
})

export const useRestoreForecastMutation = makeAxiosMutation({
  serviceCall: ForecastService.restoreForecast,
  invalidates: () => [forecastsBaseKey, aircraftBaseKey],
})

export const useDeleteForecastMutation = makeAxiosMutation({
  serviceCall: ForecastService.deleteForecast,
  invalidates: () => [forecastsBaseKey, aircraftBaseKey],
})

export const usePinForecastMutation = makeAxiosMutation({
  serviceCall: ForecastService.pinForecast,
  invalidates: () => [aircraftBaseKey],
})

export const useExcludeForecastItemMutation = makeAxiosMutation({
  serviceCall: ForecastService.excludeForecastItem,
  invalidates: () => [forecastsBaseKey],
})

export const useConfirmForecastItemMutation = makeAxiosMutation({
  serviceCall: ForecastService.confirmForecastItem,
  invalidates: () => [forecastsBaseKey],
})

export const useIncludeForecastItemMutation = makeAxiosMutation({
  serviceCall: ForecastService.includeForecastItem,
  invalidates: () => [forecastsBaseKey],
})

export const useCreateForecastWorkOrderMutation = makeAxiosMutation({
  serviceCall: ForecastService.createForecastWorkOrder,
  invalidates: () => [workPackagesKey],
})

export const usePrintDueListMutation = makeAxiosMutation({
  serviceCall: parseBlobErrors(
    (params: { forecast_id: string; printDueList: PrintDueList }) =>
      ForecastPrintingService.printDueList(params),
  ),
  invalidates: () => [],
})
